// ============================================================================
// PITCH TYPE BREAKDOWN COMPONENT
// ============================================================================
// Comprehensive table + visual breakdown of all pitch types with velocity,
// spin rate, movement, usage, whiff rate, contact rate, and zone distribution.
// Supports both batter and pitcher perspectives.
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Chip, LinearProgress, Tooltip, TableSortLabel,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PITCH_NAMES as PITCH_TYPE_NAMES, PITCH_COLORS } from '../../utils/pitchTypes';

const COLUMNS = [
  { key: 'name', label: 'Pitch', sortable: true, align: 'left' },
  { key: 'count', label: '#', sortable: true, desc: 'Total pitches' },
  { key: 'usage', label: 'Usage%', sortable: true, format: v => `${v}%` },
  { key: 'avgVelo', label: 'Velo', sortable: true, format: v => v ? `${v}` : '—', desc: 'Avg velocity (mph)' },
  { key: 'maxVelo', label: 'Max', sortable: true, format: v => v ? `${v}` : '—', desc: 'Max velocity (mph)' },
  { key: 'avgSpin', label: 'Spin', sortable: true, format: v => v ? `${v}` : '—', desc: 'Avg spin rate (RPM)' },
  { key: 'avgHorizBreak', label: 'HB', sortable: true, format: v => v ? `${v}"` : '—', desc: 'Horizontal break (in)' },
  { key: 'avgVertBreak', label: 'VB', sortable: true, format: v => v ? `${v}"` : '—', desc: 'Vertical break (in)' },
  { key: 'swingRate', label: 'Swing%', sortable: true, format: v => `${v}%` },
  { key: 'whiffRate', label: 'Whiff%', sortable: true, format: v => `${v}%`, desc: 'Swing-and-miss rate' },
  { key: 'contactRate', label: 'Contact%', sortable: true, format: v => `${v}%` },
  { key: 'battingAvg', label: 'BA', sortable: true, format: v => v ? v.toFixed(3) : '.000', desc: 'Batting avg on BIP' },
];

// Tier classification helpers
function whiffTier(val) {
  if (val >= 35) return 4; // Elite
  if (val >= 25) return 3; // Great
  if (val >= 18) return 2; // Average
  if (val >= 12) return 1; // Below
  return 0; // Poor
}

function veloTier(val) {
  if (val >= 96) return 4;
  if (val >= 93) return 3;
  if (val >= 90) return 2;
  if (val >= 85) return 1;
  return 0;
}

const TIER_COLORS = [
  'rgba(211,47,47,0.85)',   // Poor
  'rgba(239,108,0,0.85)',   // Below
  'rgba(255,179,0,0.90)',   // Avg
  'rgba(102,187,106,0.90)', // Above
  'rgba(46,125,50,0.95)'    // Elite
];

/**
 * PitchTypeBreakdown - Detailed pitch arsenal breakdown
 * @param {Object} props
 * @param {Object} props.pitchTypes - Object keyed by pitch type code with stats
 * @param {Object} props.battedBall - Optional batted ball data by pitch type
 * @param {string} props.perspective - 'batter' or 'pitcher'
 */
const PitchTypeBreakdown = ({ pitchTypes = {}, battedBall = {}, perspective = 'pitcher', onPitchTypeClick }) => {
  const theme = useTheme();
  const [sortKey, setSortKey] = useState('usage');
  const [sortDir, setSortDir] = useState('desc');

  const rows = useMemo(() => {
    return Object.entries(pitchTypes)
      .filter(([code]) => code !== 'UN' || pitchTypes[code]?.count > 5)
      .map(([code, data]) => ({
        code,
        name: data.name || PITCH_TYPE_NAMES[code] || code,
        color: PITCH_COLORS[code] || '#9e9e9e',
        count: data.count || 0,
        usage: data.usage || 0,
        avgVelo: data.avgVelo || 0,
        maxVelo: data.maxVelo || 0,
        avgSpin: data.avgSpin || 0,
        avgHorizBreak: data.avgHorizBreak || 0,
        avgVertBreak: data.avgVertBreak || 0,
        swingRate: data.swingRate || 0,
        whiffRate: data.whiffRate || 0,
        contactRate: data.contactRate || 0,
        battingAvg: data.battingAvg || 0,
        strikes: data.strikes || 0,
        balls: data.balls || 0,
        inPlay: data.inPlay || 0,
        hits: data.hits || 0,
        bb: battedBall?.[code] || null,
      }))
      .sort((a, b) => {
        const av = a[sortKey] ?? 0;
        const bv = b[sortKey] ?? 0;
        if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortDir === 'asc' ? av - bv : bv - av;
      });
  }, [pitchTypes, battedBall, sortKey, sortDir]);

  const totalPitches = rows.reduce((s, r) => s + r.count, 0);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No pitch type data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Pitch Arsenal {perspective === 'pitcher' ? '(Thrown)' : '(Faced)'}
        </Typography>
        <Chip label={`${rows.length} pitch types • ${totalPitches.toLocaleString()} total`} size="small" variant="outlined" />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell
                  key={col.key}
                  align={col.align || 'center'}
                  sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }}
                >
                  {col.sortable ? (
                    <Tooltip title={col.desc || col.label}>
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDir : 'desc'}
                        onClick={() => handleSort(col.key)}
                        sx={{ '& .MuiTableSortLabel-icon': { fontSize: '0.75rem' } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </Tooltip>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow
                key={row.code}
                hover
                onClick={onPitchTypeClick ? () => onPitchTypeClick(row.code) : undefined}
                sx={{
                  '&:last-child td': { border: 0 },
                  cursor: onPitchTypeClick ? 'pointer' : 'default',
                }}
              >
                {/* Pitch name with color indicator */}
                <TableCell sx={{ px: 1, py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: row.color, flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight="bold" noWrap>{row.name}</Typography>
                  </Box>
                </TableCell>

                {/* Count */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.count}</Typography>
                </TableCell>

                {/* Usage% with mini bar */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 60 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(row.usage, 100)}
                      sx={{
                        flex: 1, height: 6, borderRadius: 3,
                        backgroundColor: alpha(row.color, 0.15),
                        '& .MuiLinearProgress-bar': { backgroundColor: row.color, borderRadius: 3 }
                      }}
                    />
                    <Typography variant="caption" sx={{ minWidth: 28, fontSize: '0.75rem' }}>{row.usage}%</Typography>
                  </Box>
                </TableCell>

                {/* Velocity */}
                <TableCell align="center" sx={{ px: 1, py: 0.5, backgroundColor: row.avgVelo ? alpha(TIER_COLORS[veloTier(row.avgVelo)], 0.1) : 'transparent' }}>
                  <Typography variant="caption" fontWeight={row.avgVelo >= 95 ? 'bold' : 'normal'}>{row.avgVelo || '—'}</Typography>
                </TableCell>

                {/* Max Velo */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.maxVelo || '—'}</Typography>
                </TableCell>

                {/* Spin */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.avgSpin || '—'}</Typography>
                </TableCell>

                {/* Horizontal Break */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.avgHorizBreak ? `${row.avgHorizBreak}"` : '—'}</Typography>
                </TableCell>

                {/* Vertical Break */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.avgVertBreak ? `${row.avgVertBreak}"` : '—'}</Typography>
                </TableCell>

                {/* Swing% */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.swingRate}%</Typography>
                </TableCell>

                {/* Whiff% with color tier */}
                <TableCell align="center" sx={{
                  px: 1, py: 0.5,
                  backgroundColor: alpha(TIER_COLORS[perspective === 'pitcher' ? whiffTier(row.whiffRate) : 4 - whiffTier(row.whiffRate)], 0.15)
                }}>
                  <Typography variant="caption" fontWeight="bold">{row.whiffRate}%</Typography>
                </TableCell>

                {/* Contact% */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.contactRate}%</Typography>
                </TableCell>

                {/* BA */}
                <TableCell align="center" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption">{row.battingAvg ? row.battingAvg.toFixed(3) : '.000'}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PitchTypeBreakdown;
